import type { ReactNode } from 'react';
import { FormSelect, SegmentedControl } from '@/components/UI';
import {
    availableWindowRoles,
    formatHorizonLabel,
    metricOptionsForLens,
    WINDOW_ROLE_LABELS,
    type IdeaLabRunReport,
    type ReportMetric
} from '../ideaLabUtils';
import { availableMarketGroups, horizonsForLens, type ReportView, type ReportViewMode } from '../reportView';

interface ReportViewControlsProps {
    report: Pick<IdeaLabRunReport, 'cells' | 'horizons'>;
    view: ReportView;
    onChange: (patch: Partial<ReportView>) => void;
    /** Offer the by-horizon matrix next to the scoreboard (run page only). */
    showModeToggle?: boolean;
}

const MODE_OPTIONS: { value: ReportViewMode; label: string }[] = [
    { value: 'scoreboard', label: 'Scoreboard' },
    { value: 'horizons', label: 'By horizon' }
];

const ControlGroup = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="flex flex-col gap-1">
        <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
        {children}
    </div>
);

/** A choice with a single option shows as a fixed label, so the slice on screen is always named. */
const ChoiceGroup = <T extends string>({
    label,
    options,
    value,
    onChange
}: {
    label: string;
    options: { value: T; label: string }[];
    value: T;
    onChange: (value: T) => void;
}) => (
    <ControlGroup label={label}>
        {options.length > 1 ? (
            <SegmentedControl ariaLabel={label} options={options} value={value} onChange={onChange} />
        ) : (
            <span className="inline-flex h-[30px] items-center rounded-md border border-zinc-800 px-3 text-sm text-zinc-300">
                {options[0]?.label ?? '—'}
            </span>
        )}
    </ControlGroup>
);

/** One row of report slice controls; a group with a single choice shows it as a fixed label. */
const ReportViewControls = ({ report, view, onChange, showModeToggle = false }: ReportViewControlsProps) => {
    const groups = availableMarketGroups(report);
    const windowRoles = availableWindowRoles(report);
    const horizons = horizonsForLens(report, view.lens);
    const metricOptions = metricOptionsForLens(view.lens);

    return (
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <ChoiceGroup
                label="Market group"
                options={groups.map((group) => ({ value: `${group.entityType}:${group.lens}`, label: group.label }))}
                value={`${view.entityType}:${view.lens}`}
                onChange={(value) => {
                    const group = groups.find((candidate) => `${candidate.entityType}:${candidate.lens}` === value);
                    if (group) onChange({ entityType: group.entityType, lens: group.lens });
                }}
            />
            <ChoiceGroup
                label="Window"
                options={windowRoles.map((role) => ({ value: role, label: WINDOW_ROLE_LABELS[role] }))}
                value={view.windowRole}
                onChange={(windowRole) => onChange({ windowRole })}
            />
            {view.mode === 'scoreboard' && (
                <ChoiceGroup
                    label="Horizon"
                    options={horizons.map((horizon) => ({ value: String(horizon), label: formatHorizonLabel(horizon) }))}
                    value={String(view.horizonSeconds)}
                    onChange={(value) => onChange({ horizonSeconds: Number(value) })}
                />
            )}
            {showModeToggle && (
                <div className="ml-auto flex flex-wrap items-end gap-3">
                    {view.mode === 'horizons' && (
                        <ControlGroup label="Metric">
                            <FormSelect
                                aria-label="Metric"
                                fullWidth={false}
                                noMargin
                                options={metricOptions.map((option) => ({ value: option.value, label: option.label }))}
                                size="sm"
                                title={metricOptions.find((option) => option.value === view.metric)?.description}
                                value={view.metric}
                                onChange={(event) =>
                                    onChange({
                                        metric:
                                            metricOptions.find((option) => option.value === event.target.value)?.value ??
                                            ('meanReturnPercent' satisfies ReportMetric)
                                    })
                                }
                            />
                        </ControlGroup>
                    )}
                    <ControlGroup label="View">
                        <SegmentedControl
                            ariaLabel="Report view"
                            options={MODE_OPTIONS}
                            value={view.mode}
                            onChange={(mode) => onChange({ mode })}
                        />
                    </ControlGroup>
                </div>
            )}
        </div>
    );
};

export default ReportViewControls;
